import * as intl from "@arcgis/core/intl";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { isWidgetDisplayed } from "../../esri/widgets/expandUtils";
import { setMobileApp } from "../../redux/action/MobileReducer-action";
import { view } from "../../utils/API";
import { ConfigContext } from "../../utils/ConfigContext";
import { EMBED_VARIANTS, getConfig } from "../../utils/helper";
import { addSymbology } from "../../utils/symbologies";
import { snackOptions } from "../App/App";
import { StyledLoader } from "../App/App-styled";
import { useSnackbar } from "../SnackBar";

const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState();
  const [activeLanguage, setActiveLanguage] = useState();
  const [searchListText, setSearchListText] = useState("");
  const { i18n, t } = useTranslation("common");
  const [openSnackbar] = useSnackbar(snackOptions); //reloads component

  const dispatch = useDispatch();

  //console.debug("Rendering with window dimensions: " + windowWidth + "/" + windowHeight)
  window.isSmall = useMediaQuery({ maxWidth: 768 });
  window.isLarge = useMediaQuery({ minWidth: 1024, maxWidth: 1599 });
  window.isExtraLarge = useMediaQuery({ minWidth: 1600 });
  window.isMedium = !window.isSmall && !window.isLarge && !window.isExtraLarge;

  useEffect(() => {
    //Initialize configuration
    const conf = getConfig(t, openSnackbar);
    if (
      conf.id === "vanuatu" ||
      (Array.isArray(conf.alias)
        ? conf.alias.includes("vanuatu")
        : conf.alias === "vanuatu")
    ) {
      openSnackbar(
        <div className="layer-error">
          <span>
            This is a demo page for the{" "}
            <span className="layer-error__names">HNPW 2024</span>, showcasing
            the benefits of integrating humanitarian information systems. The
            data provided is combining information from{" "}
            <span className="layer-error__names">
              STOCKHOLM, Dubai Humanitarian, ASYREC, and LogIE
            </span>
            . See the links in the dashboard for further information
          </span>
        </div>,
        60 * 10000
      );
    }

    let language = i18n.language?.toLowerCase();
    if (!isWidgetDisplayed(conf, "LanguageSwitcher") && language !== "en") {
      language = "en";
    }

    if (
      conf.supportedLngs &&
      Array.isArray(conf.supportedLngs) &&
      !conf.supportedLngs.includes(language) &&
      conf.supportedLngs.length > 0
    ) {
      language = conf.supportedLngs[0];
    }

    setActiveLanguage(language || "en");

    const handleOffline = () =>
      openSnackbar(t("screen.message.offline"), 15000);
    const handleOnline = () => openSnackbar(t("screen.message.online"), 15000);
    const handleSwUpdate = () =>
      openSnackbar(t("screen.message.newVersion"), 15000);

    if (conf?.embed !== EMBED_VARIANTS.APP) {
      window.addEventListener("offline", handleOffline);
      window.addEventListener("online", handleOnline);
    }

    window.addEventListener("swUpdatefound", handleSwUpdate);

    //After changing language, translated labels should be updated
    view?.map.layers.forEach((l) => {
      addSymbology(l, config, t);
    });

    return () => {
      if (conf?.embed !== EMBED_VARIANTS.APP) {
        window.removeEventListener("offline", handleOffline);
        window.removeEventListener("online", handleOnline);
      }

      window.removeEventListener("swUpdatefound", handleSwUpdate);
    };
  }, [i18n.language]);

  useEffect(() => {
    if (!activeLanguage) return;
    i18n
      .changeLanguage(activeLanguage, () => {
        const conf = getConfig(t, openSnackbar);
        document.body.style.setProperty("--opsColor", conf.opsColor);
        setConfig(conf);
        const isMobileApp = !!conf.embed || window.isSmall;
        dispatch(setMobileApp(isMobileApp));

        intl.setLocale(i18n.language);
      })
      .then(() => {});
  }, [activeLanguage]);

  if (!config || !activeLanguage)
    return (
      <StyledLoader
        text="Loading..."
        id="app-loader"
        data-testid="app-loader"
      />
    );

  return (
    <ConfigContext.Provider
      value={{
        config,
        setConfig,
        searchListText,
        setSearchListText,
        activeLanguage,
        setActiveLanguage,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export default AppConfigProvider;
